"use client";

import { ColumnDef, SortingState, flexRender, getCoreRowModel, ColumnFiltersState, getFilteredRowModel, getSortedRowModel, useReactTable, Row } from "@tanstack/react-table";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { Input } from "@/components/ui/input";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Model } from "./columns";
import { Button } from "../../../components/ui/button";
import { authClient, hasPermission } from "../../../lib/auth-client";
import { useUpdateModel, useDeleteModel } from "../../../api/routes/models";
import { Pencil, Trash2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../../../components/ui/dialog";
import { toast } from "react-toastify";

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
}

export function DataTable<TData, TValue>({ columns, data }: DataTableProps<TData, TValue>) {
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [selectedModel, setSelectedModel] = useState<Model | null>(null);
    const [editName, setEditName] = useState("");

    const navigate = useNavigate();
    const updateModel = useUpdateModel();
    const deleteModel = useDeleteModel();

    const { data: session } = authClient.useSession();
    const role = (session?.user as any)?.role;

    const canEdit = role && hasPermission(role, { model: ["configure"] });
    const canDelete = role && hasPermission(role, { model: ["delete"] });

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            sorting,
            columnFilters,
        },
    });

    const handleRowClick = (row: Row<Model>) => {
        navigate(`/models/${row.original.id}`);
    };

    const handleEdit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedModel || !editName.trim()) {
            toast.error("Model name is required");
            return;
        }
        updateModel.mutate(
            { id: selectedModel.id, data: { name: editName.trim() } },
            {
                onSuccess: () => {
                    toast.success("Model updated successfully");
                    setEditOpen(false);
                },
                onError: (error: any) => {
                    toast.error(
                        error?.response?.data?.message ||
                            error.message ||
                            "Failed to update model"
                    );
                },
            }
        );
    };

    const handleDelete = () => {
        if (!selectedModel) return;
        deleteModel.mutate(selectedModel.id, {
            onSuccess: () => {
                toast.success("Model deleted successfully");
                setDeleteOpen(false);
            },
            onError: (error: any) => {
                toast.error(
                    error?.response?.data?.message ||
                        error.message ||
                        "Failed to delete model"
                );
            },
        });
    };

    return (
        <>
            <div className="flex items-center justify-between py-4">
                <Input placeholder="Filter models by name" value={(table.getColumn("name")?.getFilterValue() as string) ?? ""} onChange={(event) => table.getColumn("name")?.setFilterValue(event.target.value)} className="max-w-sm" />
                {role && hasPermission(role, { device: ["configure"] }) && (
                    <Button
                        variant="default"
                        onClick={() => {
                            navigate("/models/register");
                        }}
                    >
                        Register New Model
                    </Button>
                )}
            </div>
            <div className="rounded-md border w-full bg-white">
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return <TableHead key={header.id}>{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}</TableHead>;
                                })}
                                {(canEdit || canDelete) && (
                                    <TableHead className="w-[100px]">
                                        <span className="sr-only">Actions</span>
                                    </TableHead>
                                )}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {data && table.getRowModel().rows?.length > 0 ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id} data-state={row.getIsSelected() && "selected"} onClick={() => handleRowClick(row as Row<Model>)} className="cursor-pointer">
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                                    ))}
                                    {(canEdit || canDelete) && (
                                        <TableCell>
                                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                {canEdit && (
                                                    <button
                                                        onClick={() => {
                                                            const model = (row as Row<Model>).original;
                                                            setSelectedModel(model);
                                                            setEditName(model.name);
                                                            setEditOpen(true);
                                                        }}
                                                        className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                                    >
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button
                                                        onClick={() => {
                                                            setSelectedModel((row as Row<Model>).original);
                                                            setDeleteOpen(true);
                                                        }}
                                                        className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                )}
                                            </div>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length + ((canEdit || canDelete) ? 1 : 0)}>No results.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Edit Model</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleEdit} className="space-y-4">
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Model Name
                            </label>
                            <input
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                required
                                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                        </div>
                        <DialogFooter className="flex justify-end gap-2 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setEditOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={updateModel.isPending}>
                                {updateModel.isPending ? "Saving..." : "Save Changes"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete Model</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-foreground">
                            Are you sure you want to permanently delete{" "}
                            <span className="font-semibold">{selectedModel?.name}</span>?
                            This action cannot be undone.
                        </p>
                    </div>
                    <DialogFooter className="flex justify-end gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setDeleteOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={deleteModel.isPending}
                        >
                            {deleteModel.isPending ? "Deleting..." : "Delete Model"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
